import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import * as z from "zod"
import { gql } from "~/__generated__"
import { getPasskey, passkeysSupported } from "~/auth/passkeys"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { useNavigateAfterAuth } from "~/auth/utils"
import { useFormErrorHandling } from "~/common/error-handling"
import { useDocumentTitle } from "~/common/use-document-title"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { updateCsrfTag } from "./utils"

const loginFormSchema = z.object({
  email: z.email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Enter your password" }),
})

type LoginFormValues = z.infer<typeof loginFormSchema>

const LOGIN_MUTATION = gql(/* GraphQL */ `
  mutation Login($input: CredentialsUserAuthInput!) {
    login: credentialsUserAuth(input: $input) {
      csrfToken
    }
  }
`)

const PASSKEY_OPTIONS_MUTATION = gql(/* GraphQL */ `
  mutation PasskeyAuthenticationOptions {
    passkeyAuthenticationOptions(input: {}) {
      optionsJson
    }
  }
`)

const PASSKEY_AUTHENTICATE_MUTATION = gql(/* GraphQL */ `
  mutation PasskeyAuthenticate($input: PasskeyAuthenticateInput!) {
    passkeyAuthenticate(input: $input) {
      csrfToken
    }
  }
`)

export const LoginScreen: React.FC = () => {
  useDocumentTitle("Sign in")
  const [login, loginResult] = useMutation(LOGIN_MUTATION)
  const [passkeyOptions] = useMutation(PASSKEY_OPTIONS_MUTATION)
  const [passkeyAuthenticate] = useMutation(PASSKEY_AUTHENTICATE_MUTATION)
  const [passkeyLoading, setPasskeyLoading] = React.useState(false)
  const {
    viewer,
    result: { refetch: refetchViewer },
  } = useViewerMaybe()
  const { navigateAfterAuth } = useNavigateAfterAuth()
  const { refetch: refetchUiAccess } = useUiAccess()

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: "", password: "" },
  })
  const { onError } = useFormErrorHandling(form)

  const finishLogin = async (csrfToken: string) => {
    updateCsrfTag(csrfToken)
    await refetchViewer()
    await refetchUiAccess()
    toast.success("Signed in")
    navigateAfterAuth()
  }

  const onSubmit = async (values: LoginFormValues) => {
    const { data, error } = await login({
      onError,
      variables: { input: { email: values.email, password: values.password, rememberMe: true } },
    })
    if (error || !data) return
    await finishLogin(data.login.csrfToken)
  }

  const signInWithPasskey = async () => {
    setPasskeyLoading(true)
    try {
      const options = await passkeyOptions()
      if (options.error || !options.data) throw options.error
      const credentialJson = await getPasskey(options.data.passkeyAuthenticationOptions.optionsJson)
      const { data, error } = await passkeyAuthenticate({
        variables: { input: { credentialJson } },
      })
      if (error || !data) throw error
      await finishLogin(data.passkeyAuthenticate.csrfToken)
    } catch (error) {
      toast.error("Passkey sign-in failed", {
        description: error instanceof Error ? error.message : "Try again or use your password.",
      })
    } finally {
      setPasskeyLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-medium tracking-tight">Sign in</h2>
        <p className="text-sm text-muted-foreground">Use a passkey or your email and password.</p>
      </div>
      {viewer ? (
        <p className="text-sm text-muted-foreground">You are already signed in.</p>
      ) : (
        <>
          {passkeysSupported() && (
            <Button
              type="button"
              className="w-full"
              onClick={signInWithPasskey}
              disabled={passkeyLoading}
            >
              {passkeyLoading ? "Waiting for passkey..." : "Sign in with a passkey"}
            </Button>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormGeneralErrors control={form.control} />
              <TextField
                control={form.control}
                name="email"
                label="Email"
                placeholder="Enter your email"
                autoComplete="username"
              />
              <TextField
                control={form.control}
                name="password"
                label="Password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <Button
                type="submit"
                variant="outline"
                className="w-full"
                disabled={loginResult.loading}
              >
                {loginResult.loading ? "Signing in..." : "Sign in with password"}
              </Button>
            </form>
          </Form>
        </>
      )}
    </div>
  )
}
